import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiLock } from 'react-icons/fi';
import Header from './Header';
import '../styles/ChangePinPage.css';

const ChangePinPage = () => {
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');

  useEffect(() => {
    if (currentPin.length === 4) {
      // Here you would validate the current PIN
      if (currentPin === '1234') { // Example validation
        setTimeout(() => {
          setStep(2);
          setError('');
        }, 300);
      } else {
        setError('Incorrect PIN');
        setCurrentPin('');
      }
    }
  }, [currentPin]);

  useEffect(() => {
    if (step === 2 && newPin.length === 4) {
      setTimeout(() => {
        setStep(3);
        setError('');
      }, 300);
    }
  }, [newPin, step]);

  useEffect(() => {
    if (step === 3 && confirmPin.length === 4) {
      if (confirmPin === newPin) {
        // Here you would save the new PIN
        console.log('PIN changed successfully');
      } else {
        setError('PINs do not match');
        setConfirmPin('');
      }
    }
  }, [confirmPin, newPin, step]);

  const handleNumberClick = (number) => {
    setError('');
    if (step === 1 && currentPin.length < 4) {
      setCurrentPin(prev => prev + number);
    } else if (step === 2 && newPin.length < 4) {
      setNewPin(prev => prev + number);
    } else if (step === 3 && confirmPin.length < 4) {
      setConfirmPin(prev => prev + number);
    }
  };

  const handleDelete = () => {
    setError('');
    if (step === 1) {
      setCurrentPin(prev => prev.slice(0, -1));
    } else if (step === 2) {
      setNewPin(prev => prev.slice(0, -1));
    } else {
      setConfirmPin(prev => prev.slice(0, -1));
    }
  };

  const renderDots = (value) => {
    return Array(4).fill(0).map((_, index) => (
      <div
        key={index}
        className={"pin-dot " + (value.length > index ? 'filled' : '')}
      />
    ));
  };

  const getStepTitle = () => {
    switch (step) {
      case 1:
        return 'Enter Current PIN';
      case 2:
        return 'Enter New PIN';
      case 3:
        return 'Confirm New PIN';
      default:
        return '';
    }
  };

  const getStepDescription = () => {
    switch (step) {
      case 1:
        return 'Enter your current PIN to continue';
      case 2:
        return 'Choose a new 4-digit PIN';
      case 3:
        return 'Enter the new PIN again to confirm';
      default:
        return '';
    }
  };

  const numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, null, 0, 'delete'];

  return (
    <div className="change-pin-page">
      <Header title="Change PIN" className="sticky-header" />

      <div className="change-pin-content">
        <motion.div
          className="pin-card"
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        >
          <div className="pin-header">
            <motion.div
              className="header-icon"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
            >
              <FiLock />
            </motion.div>
            <h2>{getStepTitle()}</h2>
            <p className="description">{getStepDescription()}</p>
          </div>

          <div className="pin-display">
            <div className="pin-dots">
              {renderDots(
                step === 1 ? currentPin :
                  step === 2 ? newPin :
                    confirmPin
              )}
            </div>
            <AnimatePresence>
              {error && (
                <motion.p
                  className="error-message"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                >
                  {error}
                </motion.p>
              )}
            </AnimatePresence>
          </div>

          <div className="keypad">
            {numbers.map((number, index) => {
              if (number === null) return <div key={index} className="keypad-spacer" />;

              if (number === 'delete') {
                return (
                  <motion.button
                    key={index}
                    className="keypad-button delete"
                    onClick={handleDelete}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    ←
                  </motion.button>
                );
              }

              return (
                <motion.button
                  key={index}
                  className="keypad-button"
                  onClick={() => handleNumberClick(number)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {number}
                </motion.button>
              );
            })}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ChangePinPage; 